import React, { useState, useCallback } from 'react'
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material'
import {
  Delete,
  Group,
  Person,
  AttachMoney,
  PhotoCamera,
  PictureAsPdf,
  Security,
  OpenInNew,
} from '@mui/icons-material'
import { TransporterGroup, Transporter, GroupInsuranceCertificate } from '@otw/models'
import { apiClient } from '@/core/api/apiClient'

interface TransporterGroupCardProps {
  group: TransporterGroup
  transporters: Transporter[]
  avgReadyToInProgressMs?: number
  onEditGroup: (group: TransporterGroup) => void
  onDeleteGroup: (groupId: string) => void
  onOpenTemplateConfig?: (companyId: string) => void
}

const TransporterGroupCard: React.FC<TransporterGroupCardProps> = ({
  group,
  transporters,
  avgReadyToInProgressMs,
  onEditGroup,
  onDeleteGroup,
  onOpenTemplateConfig,
}) => {
  const [loadingCertPath, setLoadingCertPath] = useState<string | null>(null)

  const formatAverageDuration = (durationMs?: number) => {
    if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs <= 0) {
      return 'N/A'
    }
    const totalMinutes = Math.round(durationMs / (1000 * 60))
    const days = Math.floor(totalMinutes / (60 * 24))
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
    const minutes = totalMinutes % 60
    if (days > 0) {
      return `${days}d ${hours}h`
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  }

  // Find the owner transporter
  const owner = transporters.find(t => t.id === group.owner_id)
  
  // Find drivers and admins separately
  const drivers = transporters.filter(t => group.driver_ids.includes(t.id))
  const admins = transporters.filter(t => group.admin_ids.includes(t.id))

  const handleViewCert = useCallback(
    async (cert: GroupInsuranceCertificate) => {
      if (!cert.storagePath) return
      setLoadingCertPath(cert.storagePath)
      try {
        const { url } = await apiClient.portal.admin.getGroupInsuranceCertificateUrl(group.id, cert.storagePath)
        if (url) window.open(url, '_blank', 'noopener,noreferrer')
      } catch (err) {
        console.error('Failed to open certificate:', err)
      } finally {
        setLoadingCertPath(null)
      }
    },
    [group.id]
  )

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': { boxShadow: 3 },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Group fontSize='small' color='primary' />
            <Typography variant='h6' component='div'>
              {group.name}
            </Typography>
          </Box>
          {owner && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <Person fontSize='small' sx={{ mr: 0.5, color: 'text.secondary' }} />
              <Typography variant='body2' color='text.secondary'>
                Owner: {owner.first_name && owner.last_name 
                  ? `${owner.first_name} ${owner.last_name}` 
                  : owner.email}
              </Typography>
            </Box>
          )}
          {group.price_per_mile && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <AttachMoney fontSize='small' sx={{ mr: 0.5, color: 'text.secondary' }} />
              <Typography variant='body2' color='text.secondary'>
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(group.price_per_mile)} per mile
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size='small' color='error' onClick={() => onDeleteGroup(group.id)}>
            <Delete fontSize='small' />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Drivers Section */}
      <Typography variant='subtitle2' gutterBottom>
        Drivers ({drivers.length}):
      </Typography>
      {drivers.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mt: 1,
            maxHeight: 150,
            overflowY: 'auto',
            '& > div': {
              p: 1,
              borderRadius: 1,
              bgcolor: 'background.default',
              borderLeft: '3px solid',
              borderColor: 'secondary.main',
            },
          }}
        >
          {drivers.map(transporter => (
            <Box
              key={transporter.id}
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Box>
                <Typography variant='body2'>
                  {transporter.first_name && transporter.last_name 
                    ? `${transporter.first_name} ${transporter.last_name}` 
                    : transporter.email}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {transporter.email}
                </Typography>
              </Box>
              <Chip
                label={transporter.status}
                color={
                  transporter.status === 'ACTIVE'
                    ? 'success'
                    : transporter.status === 'INACTIVE'
                      ? 'default'
                      : transporter.status === 'SUSPENDED'
                        ? 'error'
                        : 'default'
                }
                size='small'
              />
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant='body2' color='text.secondary'>
          No drivers in this group
        </Typography>
      )}

      {/* Admins Section */}
      <Typography variant='subtitle2' gutterBottom sx={{ mt: 2 }}>
        Admins ({admins.length}):
      </Typography>
      {admins.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mt: 1,
            maxHeight: 150,
            overflowY: 'auto',
            '& > div': {
              p: 1,
              borderRadius: 1,
              bgcolor: 'background.default',
              borderLeft: '3px solid',
              borderColor: 'primary.main',
            },
          }}
        >
          {admins.map(transporter => (
            <Box
              key={transporter.id}
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Box>
                <Typography variant='body2'>
                  {transporter.first_name && transporter.last_name 
                    ? `${transporter.first_name} ${transporter.last_name}` 
                    : transporter.email}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {transporter.email}
                </Typography>
              </Box>
              <Chip
                label={transporter.status}
                color={
                  transporter.status === 'ACTIVE'
                    ? 'success'
                    : transporter.status === 'INACTIVE'
                      ? 'default'
                      : transporter.status === 'SUSPENDED'
                        ? 'error'
                        : 'default'
                }
                size='small'
              />
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant='body2' color='text.secondary'>
          No admins in this group
        </Typography>
      )}

      {/* Insurance certificates */}
      {(group.insurance_certificates?.length ?? 0) > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant='subtitle2' gutterBottom sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Security fontSize='small' color='action' />
            Insurance certificates ({group.insurance_certificates!.length}):
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              mt: 0.5,
              '& > button': {
                p: 1,
                borderRadius: 1,
                bgcolor: 'background.default',
                borderLeft: '3px solid',
                borderColor: 'info.main',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              },
            }}
          >
            {group.insurance_certificates!.map((cert: GroupInsuranceCertificate, idx: number) => (
              <Box
                key={cert.storagePath || idx}
                component='button'
                type='button'
                onClick={() => handleViewCert(cert)}
                disabled={!!loadingCertPath}
                sx={{
                  border: 'none',
                  background: 'none',
                  cursor: loadingCertPath ? 'default' : 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  '&:hover:not(:disabled)': { bgcolor: 'action.hover' },
                  '&:disabled': { opacity: 0.8 },
                }}
              >
                <PictureAsPdf sx={{ fontSize: 18, color: 'error.main', flexShrink: 0 }} />
                <Typography variant='body2' sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {cert.fileName}
                </Typography>
                {loadingCertPath === cert.storagePath ? (
                  <CircularProgress size={16} sx={{ flexShrink: 0 }} />
                ) : (
                  <OpenInNew sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ mt: 'auto' }}>
        <Box
          sx={{
            mt: 2,
            px: 1.5,
            py: 1.25,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'primary.light',
            background: 'linear-gradient(135deg, rgba(25,118,210,0.14) 0%, rgba(25,118,210,0.06) 100%)',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(25,118,210,0.12)',
          }}
        >
          <Typography
            variant='caption'
            sx={{ color: 'primary.dark', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}
          >
            Pickup time
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.6 }}>
            <Box
              sx={{
                px: 1.5,
                py: 0.35,
                borderRadius: 999,
                bgcolor: 'rgba(25,118,210,0.18)',
                border: '1px solid rgba(25,118,210,0.28)',
              }}
            >
              <Typography variant='h6' sx={{ color: 'primary.dark', lineHeight: 1.1, fontWeight: 700 }}>
                {formatAverageDuration(avgReadyToInProgressMs)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          {onOpenTemplateConfig && (
            <Button 
              variant='outlined' 
              size='small' 
              startIcon={<PhotoCamera />}
              onClick={() => onOpenTemplateConfig(group.id)}
            >
              Photo Templates
            </Button>
          )}
          <Button variant='contained' size='small' onClick={() => onEditGroup(group)}>
            Manage Group
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}

export default TransporterGroupCard
